import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Split, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { DissolveSummary } from "../client/DissolveSummary";
import { api, errorMessage } from "../../lib/api";
import { notifyBandejaChanged } from "../../lib/brand";

export const ReviewClients = ({ onChanged }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [job, setJob] = useState(null);
  const load = useCallback(() => api.get("/client-admin/review").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const finished = useCallback((j) => {
    setJob(null);
    api.post(`/client-admin/repartir/${j.id}/dismiss`).catch(() => {});
    if (j.status === "terminado") setSummary(j.result);
    else toast.error(j.error || "No se pudo terminar el reparto");
    notifyBandejaChanged();
    onChanged();
    load();
  }, [load, onChanged]);
  // REQ-315: "Repartir todos" runs on the server; reopening the page picks the running job up again
  useEffect(() => {
    api.get("/client-admin/repartir/current").then((r) => r.data && (r.data.status === "en_curso" ? setJob(r.data) : finished(r.data))).catch(() => {});
  }, [finished]);
  useEffect(() => {
    if (!job) return undefined;
    const t = setInterval(() => api.get(`/client-admin/repartir/${job.id}`).then((r) => (r.data.status === "en_curso" ? setJob(r.data) : finished(r.data))).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [job?.id, finished]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    const c = confirm;
    setConfirm(null);
    setBusy(true);
    try {
      if (c === "all") setJob((await api.post("/client-admin/review/dissolve-all")).data);
      else {
        setSummary((await api.post(`/client-admin/${encodeURIComponent(c.borrower_id)}/dissolve`, null, { timeout: 300000 })).data);
        notifyBandejaChanged();
        onChanged();
      }
    } catch (err) {
      toast.error(errorMessage(err, "No se pudieron repartir los archivos, intenta de nuevo"));
    }
    setBusy(false);
    load();
  };
  const progress = job && (
    <div className="space-y-1.5 rounded-[12px] border border-[#e8edf5] bg-[#f8fafd] px-3 py-2.5" data-testid="repartir-job-progress">
      <div className="flex items-center justify-between text-xs font-semibold text-[#182235]">
        <span className="flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-[#0f5ef7]" /> Repartiendo cliente {Math.min(job.processed + 1, job.total)} de {job.total}{job.current ? ` · ${job.current}` : ""}</span>
        <span data-testid="repartir-job-count">{job.processed}/{job.total}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[#e8edf5]"><div className="h-full bg-[#0f5ef7] transition-[width] duration-500" style={{ width: `${job.total ? (100 * job.processed) / job.total : 0}%` }} /></div>
      <p className="text-[11px] text-[#5b667a]" data-testid="repartir-job-updated">Archivados {job.counts.asignados || 0} · ya existían {job.counts.ya_existian || 0} · a Por clasificar {job.counts.por_clasificar || 0} · última actualización {new Date(job.updated_at).toLocaleTimeString("es-MX")}. Puedes recargar la página; sigue en el servidor.</p>
    </div>
  );
  if (!items?.length) return job ? <section className="tr-card p-4">{progress}</section> : <DissolveSummary result={summary} onClose={() => setSummary(null)} />;
  const total = items.reduce((n, c) => n + c.files + c.pending, 0);
  return (
    <section className="tr-card space-y-3 border-[#f3d695] p-4 sm:p-5" data-testid="review-clients-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-bold text-[#182235]" data-testid="review-clients-title">
          <TriangleAlert className="h-4 w-4 text-[#b54708]" /> Clientes por revisar ({items.length})
        </h2>
        <button type="button" disabled={busy || !!job} onClick={() => setConfirm("all")} className="tr-btn-light px-3 py-2 text-xs" data-testid="review-dissolve-all-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Split className="h-3.5 w-3.5" />} Repartir todos
        </button>
      </div>
      {progress}
      <p className="text-xs text-[#5b667a]">Clientes creados desde carpetas cuyo nombre no parece de un cliente (CONTRATOS, DOCUMENTOS, EXPEDIENTE, SOFOM, FALTANTES, FIDEICOMISO) o que son subcarpetas de un cliente (empiezan con número o dicen PRÉSTAMO, MUTUO, DISPERSIÓN, LÍNEA DE CRÉDITO, BORRADOR) y sin créditos. Repartir envía cada archivo al cliente de su carpeta original.</p>
      <ul className="space-y-1.5">
        {items.map((c, i) => (
          <li key={c.borrower_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#fffaf0] px-3 py-2 text-sm" data-testid={`review-client-${i}`}>
            <button type="button" onClick={() => navigate(`/clientes/${encodeURIComponent(c.borrower_id)}`)} className="text-left font-semibold text-[#0f5ef7] hover:underline" data-testid={`review-client-link-${i}`}>
              {c.name} <span className="font-mono text-xs text-[#5b667a]">{c.borrower_id}</span>
            </button>
            <span className="rounded-full bg-[#fdebc8] px-2 py-0.5 text-[11px] font-semibold text-[#8a5a00]" data-testid={`review-client-reason-${i}`}>{c.reason}</span>
            <span className="flex items-center gap-3 text-xs text-[#5b667a]">
              {c.files} en expediente · {c.pending} en Por clasificar
              <button type="button" disabled={busy} onClick={() => setConfirm(c)} className="tr-btn-light px-2.5 py-1.5 text-xs" data-testid={`review-dissolve-${i}`}>Repartir archivos y eliminar</button>
            </span>
          </li>
        ))}
      </ul>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="review-dissolve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "all" ? `¿Repartir los archivos de ${items.length} clientes?` : `¿Repartir archivos de "${confirm?.name}"?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "all" ? `${total} archivos` : `${(confirm?.files || 0) + (confirm?.pending || 0)} archivos`} se asignarán al cliente cuyo nombre aparezca en el archivo o sus carpetas; los demás irán a Por clasificar. Luego se eliminarán {confirm === "all" ? "estos clientes" : "el cliente"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="review-dissolve-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={run} className="bg-[#b42318] text-white hover:bg-[#912018]" data-testid="review-dissolve-confirm">Repartir y eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DissolveSummary result={summary} onClose={() => setSummary(null)} />
    </section>
  );
};
